import type { ActivityTask } from "../store/useActivityTaskStore";

// A "meeting" is any activity whose category is named "Meeting".
export const isMeetingActivity = (a: ActivityTask): boolean =>
  // typeof null === "object" — a deleted/unset category must not crash the dashboard.
  !!a.category &&
  typeof a.category === "object" &&
  /meeting/i.test((a.category as { name?: string }).name || "");

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export const isToday = (iso?: string | null): boolean => {
  if (!iso) return false;
  return startOfDay(new Date(iso)) === startOfDay(new Date());
};

// Upcoming = due today or any future day (past meetings are dropped).
export const upcomingMeetings = (activities: ActivityTask[]): ActivityTask[] =>
  activities
    .filter(
      (a) =>
        isMeetingActivity(a) &&
        a.dueDate &&
        startOfDay(new Date(a.dueDate)) >= startOfDay(new Date())
    )
    .sort((a, b) => +new Date(a.dueDate as string) - +new Date(b.dueDate as string));

// Past meetings that still need minutes recorded — due before today and with
// no refined minutes yet. Bounded to the last 30 days so old meetings don't
// nag forever. Newest-first so the most recent meeting prompts at the top.
export const pastMeetingsNeedingMinutes = (activities: ActivityTask[]): ActivityTask[] => {
  const today = startOfDay(new Date());
  const cutoff = today - 30 * 86_400_000;
  return activities
    .filter(
      (a) =>
        isMeetingActivity(a) &&
        a.dueDate &&
        startOfDay(new Date(a.dueDate)) < today &&
        startOfDay(new Date(a.dueDate)) >= cutoff &&
        !a.meetingMinutes?.refinedAt
    )
    .sort((a, b) => +new Date(b.dueDate as string) - +new Date(a.dueDate as string));
};

// Returns the linked client's name, or "" when the meeting has no client
// (callers should hide the "Client:" line entirely in that case).
export const meetingClient = (a: ActivityTask): string => {
  if (a.relatedLead && typeof a.relatedLead === "object") {
    return [a.relatedLead.name, a.relatedLead.company].filter(Boolean).join(" · ");
  }
  return "";
};

export const meetingDateLabel = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diffDays = Math.round((day - today) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};
